// La mia casella email — configurazione SMTP/IMAP personale (invio dalle proprie caselle aziendali)
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Save, Send, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = {
  giotto: { label: "Giotto Carni", smtp_host: "mail.giottocarni.com", smtp_port: 465, smtp_security: "ssl", imap_host: "mail.giottocarni.com", imap_port: 993 },
  aruba: { label: "Aruba", smtp_host: "smtps.aruba.it", smtp_port: 465, smtp_security: "ssl", imap_host: "imaps.aruba.it", imap_port: 993 },
  gmail: { label: "Gmail / Workspace", smtp_host: "smtp.gmail.com", smtp_port: 465, smtp_security: "ssl", imap_host: "imap.gmail.com", imap_port: 993 },
  outlook: { label: "Outlook / 365", smtp_host: "smtp.office365.com", smtp_port: 587, smtp_security: "starttls", imap_host: "outlook.office365.com", imap_port: 993 },
};

const EMPTY = { enabled: false, from_name: "", smtp_host: "", smtp_port: 465, smtp_user: "", smtp_password: "", smtp_security: "ssl", imap_host: "", imap_port: 993, imap_save_sent: false };

export default function EmailSettingsCard() {
  const [form, setForm] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/email-settings`).then((r) => {
      setForm({ ...EMPTY, ...r.data, smtp_password: "" });
      setHasPassword(!!r.data.has_password);
    }).catch(() => setForm({ ...EMPTY }));
  }, []);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const applyPreset = (p) => setForm((f) => ({ ...f, ...PRESETS[p], label: undefined }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-settings`, { ...form, smtp_port: parseInt(form.smtp_port) || 465, imap_port: parseInt(form.imap_port) || 993 });
      toast.success("Impostazioni email salvate");
      if (form.smtp_password) setHasPassword(true);
      set("smtp_password", "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/email-settings/test`);
      toast.success(r.data.detail || "Email di prova inviata!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fallito");
    } finally { setTesting(false); }
  };

  return (
    <Card className="bg-white border-stone-200" data-testid="email-settings-card">
      <CardHeader className="border-b border-stone-200">
        <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-[#C5A059]" />La mia casella email (SMTP/IMAP)</CardTitle>
        <p className="text-xs text-stone-500 font-normal">Le email del portale (notifiche ordini, comunicazioni ai clienti) partiranno dalla tua casella aziendale invece che dal mittente generico.</p>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-stone-600 mr-1">Compila con:</span>
          {Object.entries(PRESETS).map(([k, p]) => (
            <Button key={k} type="button" size="sm" variant="outline" onClick={() => applyPreset(k)} data-testid={`email-preset-${k}`}>{p.label}</Button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-stone-700">Indirizzo email / utente SMTP *</Label>
            <Input value={form.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} placeholder="nome@tuaazienda.it" data-testid="email-smtp-user" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-stone-700">Password casella * {hasPassword && !form.smtp_password && <span className="text-emerald-600 font-normal">(salvata — lascia vuoto per non cambiarla)</span>}</Label>
            <Input type="password" value={form.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} placeholder={hasPassword ? "••••••••" : "password"} data-testid="email-smtp-password" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-stone-700">Nome mittente</Label>
            <Input value={form.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="Mario Rossi — Giotto Carni" data-testid="email-from-name" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label className="text-xs font-semibold text-stone-700">Server SMTP *</Label>
              <Input value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtps.aruba.it" data-testid="email-smtp-host" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-700">Porta</Label>
              <Input type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} data-testid="email-smtp-port" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-stone-700">Sicurezza</Label>
            <Select value={form.smtp_security} onValueChange={(v) => set("smtp_security", v)}>
              <SelectTrigger data-testid="email-smtp-security"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ssl">SSL (porta 465)</SelectItem>
                <SelectItem value="starttls">STARTTLS (porta 587)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t border-stone-100 pt-3 space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox checked={form.imap_save_sent} onCheckedChange={(v) => set("imap_save_sent", !!v)} className="mt-0.5" data-testid="email-imap-save-sent" />
            <span className="text-sm text-stone-700">Salva le email inviate anche nella mia <b>Posta inviata</b> (IMAP) — così le vedi dal telefono e dal PC</span>
          </label>
          {form.imap_save_sent && (
            <div className="grid grid-cols-3 gap-2 max-w-md">
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-stone-700">Server IMAP</Label>
                <Input value={form.imap_host} onChange={(e) => set("imap_host", e.target.value)} placeholder="imaps.aruba.it" data-testid="email-imap-host" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-700">Porta</Label>
                <Input type="number" value={form.imap_port} onChange={(e) => set("imap_port", e.target.value)} data-testid="email-imap-port" />
              </div>
            </div>
          )}
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer border-t border-stone-100 pt-3">
          <Checkbox checked={form.enabled} onCheckedChange={(v) => set("enabled", !!v)} className="mt-0.5" data-testid="email-enabled" />
          <span className="text-sm text-stone-700"><b>Attiva l'invio dalla mia casella</b> — se spento (o se l'invio fallisce) il portale usa il mittente generico</span>
        </label>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={save} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="email-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva
          </Button>
          <Button onClick={test} disabled={testing} variant="outline" data-testid="email-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Invia email di prova
          </Button>
        </div>
        <p className="text-[11px] text-stone-400">Nota Gmail: serve una "password per le app" (Account Google → Sicurezza → Verifica in due passaggi → Password per le app).</p>
      </CardContent>
    </Card>
  );
}
